import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Loader2 } from "lucide-react"
import { useEffect, useState } from "react"
import { useNumberFormat } from "../../../../home/useNumberFormat"
import {
  indexNodes,
  phaseStatuses,
  type PhaseStatus,
} from "../../studio/runProgress"
import { useRunPoll } from "../../studio/useRunPoll"

type TKey = Parameters<ReturnType<typeof useTranslation>["translate"]>[0]

/** How often the elapsed line recomputes while nothing else forces a re-render. */
const TICK_MS = 30_000

/** Phase status → its translation key. `done` covers both `ok` and `flagged`
 *  rows: both mean the phase produced a value, and this feed is a glance at
 *  phases, not the board's finer verdict colouring. */
const STATUS_LABEL: Record<PhaseStatus, TKey> = {
  queued: "MC_DECL_V2_NODE_STATUS_QUEUED",
  running: "MC_DECL_V2_NODE_STATUS_RUNNING",
  done: "MC_DECL_V2_NODE_STATUS_DONE",
  failed: "MC_DECL_V2_NODE_STATUS_FAILED",
}

/**
 * B5'S WAITING BODY (TUR-1211, Erik 02.09): a spinner and one sentence were the
 * whole story for a run that can take up to two hours before the backend marks
 * it failed. Mounting this component — and ONLY this, only in the awaiting
 * branch of `StudioV2Layout` — is what ARMS `useRunPoll` (`watchOnMount: true`):
 * the hook already exists for the screens that show a live run, it was simply
 * never watched while B5's own body was on screen. No new backend field: the
 * elapsed line reads the declaration's own `capturedAt` the caller already has
 * (`api/types.ts`), and the feed reads the SAME node matrix the studio's
 * mission control renders — every phase this run has, in run order, each
 * with one status over all of its rows (`phaseStatuses`). Not a tail slice:
 * the endpoint sorts the matrix by kind, so "the last six rows" was whatever
 * sorted last, and a fan-out's siblings collapsed to "the latest row" could
 * read done while a sibling still ran or had failed (review, wave 2).
 * `persistent` keeps the poll alive through the empty matrix a queued run
 * shows before the worker seeds it; unmounting (the lifecycle leaving
 * pending/processing, see `StudioV2Layout`) is what stops it.
 */
export function ExtractionProgress({
  declId,
  since,
  largeScopeArticleCount = null,
}: {
  declId: string
  /** `activeDeclaration?.capturedAt ?? null` — null while the declarations
   *  list hasn't caught up yet, in which case the elapsed line is withheld
   *  rather than guessed. */
  since: string | null
  /** The supplier's article pool size when it is large enough to explain the
   *  wait (`declaration.largeScope`), null otherwise. The run checks the
   *  declaration against every one of these articles, which is what makes a
   *  big supplier's run take tens of minutes rather than a couple.
   *
   *  A FALLBACK, not the first answer: the poll below carries the same fact and
   *  reaches the case this prop cannot. An uploaded document with no pre-assigned
   *  supplier has `mc_declarations.supplier_id` null for the entire run (the
   *  pipeline commits that column only at the end), so the declaration row, which
   *  is where this prop comes from, has nothing to say for exactly the run that
   *  waits longest. The run store commits M1's answer seconds in, and the poll
   *  sizes the pool from it. */
  largeScopeArticleCount?: number | null
}) {
  const { translate } = useTranslation()
  // The app's language, not the browser's: a German UI in an English browser
  // must still read 6.132.
  const { format } = useNumberFormat()
  const run = useRunPoll(declId, { watchOnMount: true, persistent: true })
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!since) return
    const id = setInterval(() => setNow(Date.now()), TICK_MS)
    return () => clearInterval(id)
  }, [since])

  const minutes = since
    ? Math.max(0, Math.floor((now - new Date(since).getTime()) / 60_000))
    : null

  const rows = phaseStatuses(indexNodes(run.nodes))

  // The run's own answer wins where it has one: it is available while the
  // declaration column is still null, and it is the same supplier the column will
  // eventually be written from.
  const articleCount = run.largeScope
    ? run.scopeArticleCount
    : largeScopeArticleCount

  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 bg-card text-center text-[12px] text-grey5">
      <Loader2 size={16} className="animate-spin" aria-hidden="true" />
      <div>{translate("MC_DECL_V2_AWAITING_EXTRACTION")}</div>
      {minutes !== null && (
        <div>{translate("MC_DECL_V2_AWAITING_SINCE", { minutes })}</div>
      )}
      {articleCount != null && (
        <p className="max-w-[46ch] text-balance text-grey5">
          {translate("MC_DECL_PROCESSING_LARGE_SCOPE_BODY", {
            count: format(articleCount),
          })}
        </p>
      )}
      {rows.length > 0 && (
        <ul
          className="flex flex-col gap-0.5 text-grey4"
          aria-live="polite"
          aria-label={translate("MC_DECL_V2_AWAITING_EXTRACTION")}
        >
          {rows.map(({ phase, status }) => (
            <li key={phase.labelKey}>
              {translate(phase.labelKey as TKey)} ·{" "}
              {translate(STATUS_LABEL[status])}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
